export const COMPANY = {
  name: "Cerebral Diagnostics",
  legal: "Cerebral Diagnostics Canada Inc.",
  tagline: "Real-time brain imaging systems",
  address: "690 Queen St. East, Toronto, ON M4M 1G9",
  phone: "(647) 207-2504",
  email: "Cerebraldiagnosticscanadainc@gmail.com",
  hours: ["Monday to Friday: 9:30am to 5:00pm", "Saturday: closed", "Sunday: closed"],
  blurb:
    "Cerebral Diagnostics Canada Inc. is an Ontario-based company developing low-cost, real-time brain imaging systems and pain and sensory-loss diagnostics. We are initially targeting the diagnosis of sleep disorders and chronic pain.",
};

export const HERO = {
  label: "Toronto, Ontario · Medical imaging",
  heading: "Real-time 3D brain imaging systems",
  sub: "Cerebral Diagnostics builds low-cost, non-invasive brain imaging and pain diagnostics on everyday EEG equipment. Our DECI technology turns a standard EEG into a clear, moving 3D picture of the brain's electrical activity as it happens.",
  points: [
    "DECI: real-time 3D cortical imaging",
    "Tactile Health: objective pain and touch diagnostics",
    "Affordable, portable and non-invasive",
  ],
};

export const DECI_FEATURES = [
  {
    title: "What DECI is",
    text: "DECI captures and displays the three-dimensional electrical activity of the brain in real time using conventional, off-the-shelf EEG equipment.",
  },
  {
    title: "Real-time output",
    text: "DECI produces brain movies in real time, so the person being studied can watch their own brain activity as it happens.",
  },
  {
    title: "How it works",
    text: "DECI visualizes the rapidly changing electrical fields inside the brain as moving images, non-invasively, using a soft electrode head-cap.",
  },
  {
    title: "Why it is different",
    text: "Like fMRI, DECI can create 2D and 3D images of the brain. Unlike fMRI, it shows the rapidly changing electrical fields of the cortex as motion pictures, safely and affordably.",
  },
];

export const PEOPLE = [
  {
    name: "Dr. Mark Doidge, MD",
    role: "Founder and CEO",
    bio: "A Toronto-based medical doctor and former National Practice of Excellence award winner, recognized for founding Canadian Physicians for Aid Relief, which brought assistance to 2.5 million people across 7 countries. His front-line experience inspired an affordable, non-invasive way to objectively measure brain activity.",
  },
  {
    name: "Dr. Joseph Mocanu",
    role: "Biophysics and Software",
    bio: "Holds a doctorate in Biophysics from the University of Toronto and an MBA from the Richard Ivey School of Business. An accomplished programmer bridging deep science and product.",
  },
  {
    name: "Mr. Mario Garingo, BEng MA.Sc.",
    role: "Lead Researcher and Programmer",
    bio: "An electrical engineer pursuing a Master of Science at Ryerson University in Biomedical Signal Processing. Lead developer for the Audio-Visual Project and the Tactile Health data acquisition and reporting software.",
  },
];

export const PROJECTS = [
  "Listen To Your Mind, Ryerson University",
  "Delta Wave Analysis in Sleep, UHN Collaboration",
  "EEG Signal Classification, Algoma University",
  "The Fields Workshop, Medical Imaging",
  "The Fields-Mitacs Undergrad Summer Program",
  "Detecting current-density vector coherent movement",
];

export const DECI_PAGE = {
  intro:
    "Dynamic Electrical Cortical Imaging (DECI) is a safe, non-invasive, affordable and portable alternative to existing imaging tools such as fMRI, MRI and PET. Unlike those technologies, DECI captures and localizes the electrical activity of the cerebral cortex as it occurs in real time and displays it as 3D motion pictures.",
  how:
    "DECI captures and displays the three-dimensional electrical activity of the brain in real time using conventional, off-the-shelf EEG equipment. Like an EEG, it is inherently safe and non-invasive, requiring only electrodes on the scalp. But unlike conventional EEG, whose waves are pale reflections of the brain's underlying complexity, DECI uses a high-level mathematical transformation known as an inverse solution to produce vivid representations of moving electrical fields. Using colour and lines, DECI depicts the locations of the generators of the wave-forms seen on an EEG.",
  background:
    "The human cerebral cortex is the convoluted outer surface of the brain, the grey matter, responsible for thought, action, emotion and sensation. Because of its complexity it is susceptible to many disorders that lack proper objective diagnostic tests or require expensive equipment. Diagnosing brain disease often requires 3D localization and imaging; existing technologies such as fMRI each have strengths and weaknesses.",
  advantages: [
    "Localizes rapid changes in cortical electrical activity in three dimensions, in real time, opening the door to instant diagnosis with further development.",
    "Very inexpensive compared to fMRI, CT, MEG and PET.",
    "Safe and non-invasive.",
    "Portable. With additional funding the entire system can be made wireless, enabling brain imaging outside the stilted lab environment.",
    "Acquires over 2,000 images per second, capturing fine events that competing technologies blur together.",
  ],
  localization:
    "Cerebral Diagnostics is part of an emerging field called source localization. Since its inception roughly 20 years ago, well over 500 peer-reviewed articles have appeared on the topic. The algorithms that find brain signals in our images were created by mathematicians and continue to improve. Evidence for the newer algorithms includes evoked-potential studies, comparative studies (for example, Wennberg and Zumsteg at Toronto Western Hospital found striking overlap between PET and source localization of epileptic generators), and advanced simulation studies.",
};

export const TACTILE_PAGE = {
  about:
    "An artificial intelligence and medical device company focused on pain and touch diagnostics, and the developers of the Woodpecker, a new programmable robotic device for testing pain and tactile disorders.",
  mission:
    "To be the leader in providing diagnostic and monitoring products and services for Fibromyalgia and tactile-sensation disorders. We believe our research team has made a breakthrough in Fibromyalgia diagnosis through machine learning and AI, extracting meaningful pain-related brain signals that were previously impossible to find.",
  vision:
    "To leverage our understanding of the brain's electrical activity and translate it into a full range of diagnostics and monitoring products and services for Fibromyalgia and Neuropathy.",
  goals:
    "To improve the ease, accuracy and speed of diagnosis, and in so doing improve clinical outcomes for Fibromyalgia and Neuropathy patients.",
  woodpecker:
    "The Woodpecker is our patented, multi-use, programmable robotic arm. It was named because it pushes back and forth repeatedly, like a woodpecker's head. It was designed to deliver a precise stimulus for an objective evoked-potential test for Fibromyalgia; later functionality was added for subjective testing of mechanical pain and light-touch thresholds, important for diagnosing many types of Neuropathy.",
  patents: [
    { label: "Canadian patent CA2806771C", url: "https://patents.google.com/patent/CA2806771C/de" },
    { label: "US patent US8712512B2", url: "https://patents.google.com/patent/US8712512B2/en" },
  ],
  specSheet: { label: "Download the Woodpecker spec sheet", url: "https://drive.google.com/file/d/1Fx84c1BTr8lZihZ1qj1e2j5u-YtgeCDu/view" },
  robotDoc: { label: "Detailed description of the robotic arm for mechanical pain disorders", url: "https://docs.google.com/document/d/1zvViWgDkHEOMgf9haBxNLwYuea11EwOA-QDm2TXog_Q/edit?tab=t.0" },
  encyclopedia: { label: "Encyclopedia of Touch, Body Sense and Pain", url: "https://encyclopediaoftouchbodysenseandpain.com/" },
  results:
    "Our pilot studies suggest the basis for the first objective test for Fibromyalgia. One study involved over 600 single-trial brain-electricity reactions and was 88% accurate; a smaller 10-patient study was 95% accurate. Additional capital would let us expand study size toward stronger validation.",
  saas:
    "In the near to intermediate term we see our main income as Software as a Service for the objective diagnosis of adult Fibromyalgia: practical, affordable, internationally scalable and very safe. Any EEG technician can be trained to perform the test; after processing the EEG signals we send a detailed diagnostic report, including probability and decision-plane graphs, to the referring doctor and recording lab.",
};

export const AFFILIATES = [
  { name: "Portrait of Your Mind", url: "http://portraitofyourmind.com" },
  { name: "Headache Sciences Inc.", url: "https://headachesciences.com/" },
];

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/deci", label: "DECI" },
  { to: "/tactile-health", label: "Tactile Health" },
  { to: "/news", label: "News" },
  { to: "/investor-relations", label: "Investor Relations" },
];
